import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Sparkles, Loader2, Package, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const AiPartsRecommendation = ({ workOrder }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showAdvice, setShowAdvice] = useState(false);

  const recommend = async () => {
    setLoading(true);
    setResult(null);
    try {
      // Get vehicle info
      let vehicle = {};
      if (workOrder?.vehicle_id) {
        try {
          const vRes = await axios.get(`${API}/vehicles/${workOrder.vehicle_id}`);
          vehicle = vRes.data || {};
        } catch {}
      }

      // Determine work type from checklist
      const checklist = workOrder?.checklist || {};
      let workTypes = [];
      if (typeof checklist === 'object' && !Array.isArray(checklist)) {
        const labelMap = {
          "service_standard": "Service standard", "grand_service": "Grand service",
          "diagnostic": "Diagnostic", "changement_roues": "Changement de roues",
          "montage_pneus": "Montage pneus", "controle_expertise": "Controle expertise",
          "tolerie": "Tolerie", "peinture": "Peinture", "polish": "Polish",
        };
        workTypes = Object.entries(checklist).filter(([, v]) => v).map(([k]) => labelMap[k] || k);
      }

      const res = await axios.post(`${API}/ai/recommend-parts`, {
        brand: vehicle.brand || '',
        model: vehicle.model || '',
        year: vehicle.year || '',
        mileage: vehicle.mileage || '',
        work_type: workTypes.join(', ') || 'service standard',
        description: workOrder?.description || '',
      });
      setResult(res.data);
    } catch (err) {
      toast.error("Erreur IA: " + (err.response?.data?.detail || "Impossible d'obtenir les recommandations"));
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    if (!result) recommend();
  };

  const priorityColors = {
    haute: 'bg-red-100 text-red-700',
    moyenne: 'bg-amber-100 text-amber-700',
    basse: 'bg-slate-100 text-slate-600',
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleOpen}
        className="text-violet-700 border-violet-300 hover:bg-violet-50 gap-1.5"
        data-testid="ai-recommend-btn"
      >
        <Sparkles className="w-4 h-4" /> Recommandation IA
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
              Recommandation de pieces
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="flex flex-col items-center py-12 gap-3">
              <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
              <p className="text-sm text-slate-500">Analyse du vehicule en cours...</p>
            </div>
          ) : result?.raw_response ? (
            <Card className="p-4 bg-violet-50 border-violet-200">
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{result.conseils}</p>
            </Card>
          ) : result ? (
            <div className="space-y-4">
              {/* Parts list */}
              {result.pieces?.length > 0 ? (
                <div className="space-y-2">
                  {result.pieces.map((p, i) => (
                    <Card key={i} className="p-3 hover:shadow-sm transition-shadow" data-testid={`ai-part-${i}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2">
                          <Package className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{p.nom}</p>
                            {p.reference_generique && (
                              <p className="text-xs text-slate-500 font-mono mt-0.5">Ref: {p.reference_generique}</p>
                            )}
                            {p.marques_recommandees?.length > 0 && (
                              <p className="text-xs text-slate-400 mt-0.5">
                                Marques: {p.marques_recommandees.join(', ')}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${priorityColors[p.priorite] || priorityColors.moyenne}`}>
                            {p.priorite || 'moyenne'}
                          </span>
                          {p.estimation_prix_chf && (
                            <span className="text-xs text-emerald-600 font-medium">{p.estimation_prix_chf} CHF</span>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">Aucune piece recommandee</p>
                </div>
              )}

              {/* Advice section */}
              {result.conseils && (
                <button
                  className="w-full flex items-center justify-between p-3 bg-violet-50 rounded-lg text-left hover:bg-violet-100 transition-colors"
                  onClick={() => setShowAdvice(!showAdvice)}
                >
                  <span className="text-sm font-medium text-violet-700">Conseils de l'IA</span>
                  {showAdvice ? <ChevronUp className="w-4 h-4 text-violet-500" /> : <ChevronDown className="w-4 h-4 text-violet-500" />}
                </button>
              )}
              {showAdvice && result.conseils && (
                <p className="text-sm text-slate-600 px-3 leading-relaxed">{result.conseils}</p>
              )}

              {/* Next service */}
              {result.prochain_entretien_km && (
                <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg">
                  <span className="text-xs text-emerald-700">Prochain entretien recommande dans <strong>{result.prochain_entretien_km.toLocaleString()} km</strong></span>
                </div>
              )}

              {/* Refresh */}
              <Button variant="outline" size="sm" className="w-full" onClick={recommend} data-testid="ai-refresh-btn">
                <Sparkles className="w-4 h-4 mr-1" /> Relancer l'analyse
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};
